import { memo } from 'react';
import { Link } from 'react-router-dom';
import {
  Activity,
  Gauge,
  MessageSquare,
  Package,
  Server,
  ShoppingCart,
  type LucideIcon,
} from 'lucide-react';
import { SectionHeader } from '../ui/SectionHeader';
import { packDetailPath } from '../../utils/packRoutes';
import type { StarchartCatalogEntry, StarchartLiveStatus } from '../../types/starchart';

interface ServerCrossLinksProps {
  fqdn: string;
  catalog: StarchartCatalogEntry | null;
  live: StarchartLiveStatus | null;
}

interface CrossLink {
  to: string;
  icon: LucideIcon;
  label: string;
  description: string;
}

/**
 * The rest of ARGUS, for this server.
 *
 * The point of bringing StarChart in-house: a popularity dip is rarely interesting on its own,
 * but a popularity dip next to chunk-throttle telemetry, the modpack's changelog, and the chat
 * log for the same cluster usually explains itself. Every link here is keyed off the same FQDN
 * or cluster id the chart above is drawn from.
 */
export const ServerCrossLinks = memo(({ fqdn, catalog, live }: ServerCrossLinksProps) => {
  const clusterId = live?.clusterId;

  const links: CrossLink[] = [
    {
      to: `/chunk-throttle/${encodeURIComponent(fqdn)}`,
      icon: Gauge,
      label: 'Chunk throttle',
      description: 'Lag sources and flagged chunks',
    },
    {
      to: '/status-monitor',
      icon: Server,
      label: 'Status monitor',
      description: 'Host metrics and every instance',
    },
  ];

  if (catalog?.modpackIndexId != null) {
    links.unshift({
      to: packDetailPath({ mpi_id: catalog.modpackIndexId, apogee_id: catalog.modpackIndexId }),
      icon: Package,
      label: catalog.modpackTitle,
      description: 'Modpack detail, versions and changelog',
    });
  }

  if (clusterId) {
    links.push(
      {
        to: `/chat/${encodeURIComponent(clusterId)}`,
        icon: MessageSquare,
        label: 'Chat log',
        description: `Messages across ${clusterId}`,
      },
      {
        to: `/economy/${encodeURIComponent(clusterId)}`,
        icon: ShoppingCart,
        label: 'Economy',
        description: `Shops and transactions on ${clusterId}`,
      },
      {
        to: `/player-stream-log/${encodeURIComponent(clusterId)}`,
        icon: Activity,
        label: 'Player stream',
        description: 'Joins and leaves in real time',
      },
    );
  }

  return (
    <div className="glass-card p-4 md:p-5">
      <SectionHeader title="Elsewhere in ARGUS" icon={Activity} />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
        {links.map((link) => (
          <Link
            key={link.to}
            to={link.to}
            className="subcard p-3 flex items-center gap-3 hover:bg-glass-200/10 transition-colors"
          >
            <span className="icon-tile icon-tile-sm icon-tile-accent shrink-0">
              <link.icon className="w-4 h-4" />
            </span>
            <span className="min-w-0">
              <span className="block text-sm font-medium text-white truncate">{link.label}</span>
              <span className="block text-xs text-glass-400 truncate">{link.description}</span>
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
});

ServerCrossLinks.displayName = 'ServerCrossLinks';
